/**
 * useMediaQuery — Phase 5.75 Stage B Commit B-13
 *
 * Subscribe a React component to a CSS media query. Returns a boolean
 * that updates when the viewport crosses the breakpoint.
 *
 * Uses `useSyncExternalStore` — the React 18+ canonical pattern for
 * subscribing to external (non-React) state without the
 * `react-hooks/set-state-in-effect` rule violation that a naïve
 * useState + useEffect implementation produces.
 *
 * Usage:
 *   const isNarrow = useMediaQuery('(max-width: 767px)');
 *   if (isNarrow) return <CardGrid />;
 *
 * SSR-safe: getServerSnapshot returns `false` (no viewport on server).
 */

import { useSyncExternalStore, useCallback } from 'react';

function getSnapshot(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

export function useMediaQuery(query) {
  const subscribe = useCallback((onStoreChange) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => {};
    }
    const mql = window.matchMedia(query);
    mql.addEventListener('change', onStoreChange);
    return () => mql.removeEventListener('change', onStoreChange);
  }, [query]);

  return useSyncExternalStore(
    subscribe,
    () => getSnapshot(query),
    () => false, // SSR fallback
  );
}
